import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { PageLoader } from '../components/PageLoader';
import TaskCard, { isOpenTask, useTasks } from '../components/TaskCard';
import { findSection } from '../lib/areas';
import type { Task } from '../lib/types';

// Every task in the workspace, grouped by the business area it belongs to, with the
// ones on Home first.
export default function TasksPage() {
    const { sections } = useWorkspace();
    const { tasks, reload } = useTasks();
    const [showDone, setShowDone] = useState(false);

    if (tasks === null) return <PageLoader label="Loading the tasks..." />;

    const open = tasks.filter(isOpenTask);
    const done = tasks.filter(t => !isOpenTask(t));
    const byArea = new Map<string, Task[]>();
    for (const t of open) {
        const key = t.section_id ?? '';
        byArea.set(key, [...(byArea.get(key) ?? []), t]);
    }
    const areas = [...byArea.keys()].sort((a, b) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)));

    return (
        <div data-agent-view>
            <div className="pb-6 mb-6 border-b border-softgrey">
                <h3 className="text-lg font-semibold">Tasks</h3>
                <p className="text-sm text-coolslate mt-1">
                    Everything the business has to do, across every business area. Each area's What's Next
                    tab lists its own, and Navaris adds one when a conversation leaves a clear next step.
                </p>
            </div>

            {open.length === 0 && <p className="text-sm text-coolslate">No open tasks.</p>}

            <div className="space-y-8">
                {areas.map(area => (
                    <section key={area || 'home'}>
                        <h4 className="navaris-h3 mb-3 flex items-baseline gap-2">
                            {area
                                ? <Link to={`/workspace/${area}?tab=next`} className="hover:text-brand-dark">
                                    {findSection(sections, area)?.name ?? area}
                                  </Link>
                                : 'Home'}
                            <span className="text-[12px] font-medium text-coolslate">{byArea.get(area)!.length}</span>
                        </h4>
                        <div className="space-y-3">
                            {byArea.get(area)!.map(t => <TaskCard key={t.id} task={t} onChanged={reload} />)}
                        </div>
                    </section>
                ))}

                {done.length > 0 && (
                    <section>
                        <button
                            onClick={() => setShowDone(v => !v)}
                            aria-expanded={showDone}
                            className="flex items-baseline gap-2 navaris-h3 mb-3 hover:text-brand-dark"
                        >
                            Done
                            <span className="text-[12px] font-medium text-coolslate">{done.length}</span>
                        </button>
                        {showDone && (
                            <div className="space-y-3">
                                {done.map(t => <TaskCard key={t.id} task={t} onChanged={reload} />)}
                            </div>
                        )}
                    </section>
                )}
            </div>
        </div>
    );
}
